/**
 * MLS access: the typed seam between the screens and `src/server/mls_access_routes.py`
 * (M2, narrowed by Release 1.1 Slice A to the NTREIS group).
 *
 * HARD STOP 5 (CLAUDE.md section 8, ADR-0006): this client talks ONLY to our own same-origin
 * `/api/mls-access/*` routes. There is no MLS host, no MLS credential and no RETS/RESO anything in
 * this module. The only thing a member types is their own NTREIS member ID, which is an identifier.
 *
 * Every route answers 404 while `VALUBRIEF_MLS_ENABLED` is off, so a 404 here is the
 * "not offered on this site yet" state, not an error. Outside the NTREIS group, member routes
 * answer 403 with the one sentence `NOT_AVAILABLE_LINE`.
 */
import { apiFetch, ApiError } from "../lib/auth";

export type MlsState = "Eligible" | "Approved" | "Activated" | "Paused" | "Revoked";

/** The four-part server gate (`mls_group.data_gate`): the first missing condition, in words. */
export interface MlsGate {
  ok: boolean;
  missing: "flag" | "membership" | "activated" | "credentials" | null;
  reason: string;
}

export interface MlsMe {
  source_id: string;
  state: MlsState | null;
  explanation: string;
  mls_member_id: string | null;
  requested_at: string | null;
  updated_at: string | null;
  member: boolean;
  manager: boolean;
  gate: MlsGate;
}

export interface MlsTerms {
  version: string;
  text: string;
  /** True while the terms file is marked "PLACEHOLDER — PENDING COUNSEL" (Release 1.0.3). */
  placeholder: boolean;
  /** False while the terms block (placeholder with no owner attestation): the card says why. */
  can_accept: boolean;
  reason: string | null;
  /** Set only when the owner attestation lets members accept a placeholder file: the card shows
   *  this one line in place of the placeholder text. */
  attested_notice: string | null;
}

/**
 * One row of the caller's OWN history (`GET /me/audit`): the server's allow-list. The acting
 * manager's id, IP and request id are deliberately not typed here and nothing on the member card
 * can render them.
 */
export interface MlsAuditRow {
  at: string;
  old_state: MlsState | null;
  new_state: MlsState;
  reason: string | null;
}

/** One of the caller's OWN ledger rows (`GET /me/usage`): counts only. */
export interface MlsUsageRow {
  event: string;
  occurred_at: string;
  request_kind: string | null;
  record_count: number | null;
  report_id: string | null;
}

export interface MlsMember {
  user_id: string;
  name: string;
  email: string;
  org_id: string | null;
  open: boolean;
  added_at: string;
  added_by: string;
  reason: string | null;
  removed_at: string | null;
  removed_by: string | null;
  removed_reason: string | null;
  state: MlsState | null;
  mls_member_id: string | null;
  manager: boolean;
}

/** One agent's row on the "NTREIS usage" screen. Counts and dates only; no money anywhere. */
export interface MlsUsageReportRow {
  user_id: string;
  name: string | null;
  email: string | null;
  license: string | null;
  ntreis_member_id: string | null;
  first_connected_at: string | null;
  activated_at: string | null;
  member_during_month: boolean;
  mls_searches: number;
  mls_objects: number;
  reports_used_mls: number;
  first_use: string | null;
  last_use: string | null;
}

const quiet = { onUnauthorized: () => {} };

export const getTerms = () => apiFetch<MlsTerms>("/api/mls-access/terms", quiet);
export const getMe = () => apiFetch<MlsMe>("/api/mls-access/me");
export const getMeQuiet = () => apiFetch<MlsMe>("/api/mls-access/me", quiet);
export const getMyAudit = () =>
  apiFetch<{ audit: MlsAuditRow[] }>("/api/mls-access/me/audit").then((r) => r.audit);
export const getMyUsage = () =>
  apiFetch<{ usage: MlsUsageRow[] }>("/api/mls-access/me/usage").then((r) => r.usage);
export const requestAccess = (body: {
  mls_member_id: string;
  accept_terms: boolean;
  terms_version: string;
}) => apiFetch<MlsMe>("/api/mls-access/request", { method: "POST", body });
export const listMembers = () =>
  apiFetch<{ members: MlsMember[] }>("/api/mls-access/admin/members").then((r) => r.members);
export const addMember = (email: string, reason: string) =>
  apiFetch<{ members: MlsMember[] }>("/api/mls-access/admin/members", {
    method: "POST", body: { email, reason },
  }).then((r) => r.members);
export const removeMember = (userId: string, reason: string) =>
  apiFetch<{ members: MlsMember[] }>(
    `/api/mls-access/admin/members/${encodeURIComponent(userId)}/remove`,
    { method: "POST", body: { reason } }).then((r) => r.members);
export const transition = (userId: string, to: MlsState, reason: string) =>
  apiFetch<MlsMe>(`/api/mls-access/admin/${encodeURIComponent(userId)}/transition`, {
    method: "POST",
    body: { to, reason },
  });
export const getUsage = (month: string) =>
  apiFetch<{ month: string; columns: string[]; rows: MlsUsageReportRow[] }>(
    `/api/mls-access/admin/usage?month=${encodeURIComponent(month)}`);
export const usageCsvHref = (month: string) =>
  `/api/mls-access/admin/usage.csv?month=${encodeURIComponent(month)}`;

/** True when the feature is dark on this deployment (every route 404s). */
export const isNotOffered = (e: unknown) => e instanceof ApiError && e.status === 404;
export const isForbidden = (e: unknown) => e instanceof ApiError && e.status === 403;

/** The server's own sentence for a failed call, or a calm fallback. */
export const errorLine = (e: unknown, fallback: string) =>
  e instanceof ApiError && e.message && !/^Request failed/.test(e.message) ? e.message : fallback;

export const NOT_OFFERED_LINE = "MLS access is not offered on this site yet.";
/** The server's sentence for anyone outside the NTREIS group (`mls_group.NOT_AVAILABLE`). */
export const NOT_AVAILABLE_LINE = "MLS: not available on your account.";

/** Plain words for a state, for the badge. `null` is "none": nothing set up yet. */
export const stateWord = (s: MlsState | null) => (s === null ? "None" : s);

/** A calm date for an ISO string; an unparseable one renders as itself. */
export function formatWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Manager actions legal from each state (the server's TRANSITIONS table, labeled for a person).
 * There is no "Activate" from Approved: only the member turns MLS on, by confirming their member
 * ID and the terms on their own card.
 */
export const MANAGER_ACTIONS: Record<MlsState, { to: MlsState; label: string }[]> = {
  Eligible: [{ to: "Approved", label: "Approve" }, { to: "Revoked", label: "Revoke" }],
  Approved: [{ to: "Revoked", label: "Revoke" }],
  Activated: [{ to: "Paused", label: "Pause" }, { to: "Revoked", label: "Revoke" }],
  Paused: [{ to: "Activated", label: "Resume" }, { to: "Revoked", label: "Revoke" }],
  Revoked: [{ to: "Approved", label: "Reopen" }],
};

/** This month as `YYYY-MM`, in the viewer's calendar. */
export function thisMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** One selected MLS comp's hero photo (`POST /api/mls/photo`, Release 1.1 Slice C). The server
 *  fetches it by the listing's own key, records the image's hash in the usage ledger, and returns
 *  it as a data URI with `photo_status: "verified"`, or `photo: null` with a reason. */
export interface MlsPhoto {
  photo_status: "verified" | "unresolved";
  photo: string | null;
  photo_source?: string;
  caption?: string | null;
  reason?: string;
}
export const fetchMlsPhoto = (listingKey: string, mlsId: string) =>
  apiFetch<MlsPhoto>("/api/mls/photo", { method: "POST", body: { listing_key: listingKey, mls_id: mlsId } });

/**
 * Give each SELECTED NTREIS MLS comp that has no photo its own listing photo, in place on a copy.
 * Only rows tagged `_source: "NTREIS MLS"` are touched, so a payload with none is returned as is
 * (no request is made). A failed fetch leaves that row exactly as it was: the photo is optional.
 */
export async function withMlsPhotos<T extends { comps?: Array<Record<string, unknown>> }>(payload: T): Promise<T> {
  const comps = payload.comps ?? [];
  const todo = comps.filter((c) => c._source === "NTREIS MLS" && !c.photo && typeof c.listing_key === "string");
  if (!todo.length) return payload;
  const out = comps.map((c) => ({ ...c }));
  for (const c of out) {
    if (c._source !== "NTREIS MLS" || c.photo || typeof c.listing_key !== "string") continue;
    try {
      const p = await fetchMlsPhoto(c.listing_key as string, String(c.mls_id ?? ""));
      if (p.photo_status === "verified" && p.photo) {
        c.photo = p.photo;
        c.photo_status = "verified";
        c.photo_source = p.photo_source;
      }
    } catch {
      /* optional: the comp keeps no photo, as it would without MLS */
    }
  }
  return { ...payload, comps: out };
}
